import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Calendar, Mail, Users, ArrowRight } from 'lucide-react';
import { LandingHeader } from '@/components/layout/landing-header';

const WaitlistConfirmationPage: React.FC = () => {
  const navigate = useNavigate();

  const nextSteps = [
    {
      icon: Mail,
      title: "Check your email",
      description: "We've sent you a confirmation with next steps"
    },
    {
      icon: Calendar,
      title: "We'll review your site",
      description: "Our team will analyze your website within 24-48 hours"
    },
    {
      icon: Users,
      title: "Get early access",
      description: "You'll be among the first to try PageMind when we're ready"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <LandingHeader showBackButton={false} currentPage="waitlist" />
      
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            You're on the list! 🎉
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Thank you for your interest in PageMind. We're excited to have you join our private beta.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <h2 className="text-2xl font-semibold mb-8 text-center">What happens next?</h2>
          
          <div className="space-y-6">
            {nextSteps.map((step, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <step.icon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-8">
          <h3 className="text-lg font-semibold mb-3">While you wait...</h3>
          <p className="text-gray-700 mb-4">
            Learn more about how PageMind can transform your website's visitor experience:
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              variant="outline"
              onClick={() => navigate('/features')}
            >
              Explore Features
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/docs')}
            >
              Read Documentation
            </Button>
          </div>
        </div>

        <div className="text-center">
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')}
            className="inline-flex items-center gap-2"
          >
            Back to Home
            <ArrowRight className="h-5 w-5" />
          </Button>
          
          <p className="mt-6 text-sm text-gray-500">
            Questions? Contact us at{' '}
            <a href="mailto:hello@pagemind.ai" className="text-blue-600 hover:underline">
              hello@pagemind.ai
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default WaitlistConfirmationPage;